<template>
  <div class="py-4 px-2 p-md-4">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">Email Alerts</h5>
      <a-button type="primary" @click="openAddModal">
        <template #icon><i class="bi bi-plus-circle mr-1"></i></template>
        Add Alert Config
      </a-button>
    </div>

    <a-table
      :data-source="configs"
      :columns="columns"
      :loading="isFetching"
      row-key="id"
      :pagination="{ pageSize: 20 }"
    >
      <template #bodyCell="{ column, record }">
        <template v-if="column.key === 'device'">
          {{ getDeviceLabel(record.device) }}
        </template>
        <template v-if="column.key === 'alert_on_offline'">
          <a-tag :color="record.alert_on_offline ? 'green' : 'default'">
            {{ record.alert_on_offline ? 'Yes' : 'No' }}
          </a-tag>
        </template>
        <template v-if="column.key === 'alert_on_idle'">
          <a-tag :color="record.alert_on_idle ? 'green' : 'default'">
            {{ record.alert_on_idle ? 'Yes' : 'No' }}
          </a-tag>
        </template>
        <template v-if="column.key === 'is_active'">
          <a-tag :color="record.is_active ? 'blue' : 'default'">
            {{ record.is_active ? 'Active' : 'Inactive' }}
          </a-tag>
        </template>
        <template v-if="column.key === 'recipients'">
          <span v-if="!record.recipients || record.recipients.length === 0" class="text-muted">
            None
          </span>
          <span v-else>
            <a-tag v-for="r in record.recipients" :key="r.id" style="margin-bottom: 2px">
              {{ r.email }}
            </a-tag>
          </span>
        </template>
        <template v-if="column.key === 'actions'">
          <a-space>
            <a-button size="small" @click="openEditModal(record)">Edit</a-button>
            <a-popconfirm
              title="Delete this alert config?"
              ok-text="Yes"
              cancel-text="No"
              @confirm="handleDelete(record.id)"
            >
              <a-button size="small" danger>Delete</a-button>
            </a-popconfirm>
          </a-space>
        </template>
      </template>
    </a-table>

    <a-modal
      :visible="showModal"
      :title="editingConfig ? 'Edit Alert Config' : 'Add Alert Config'"
      centered
      ok-text="Save"
      :confirm-loading="isSubmitting"
      :ok-button-props="{ disabled: !form.device }"
      @ok="handleSubmit"
      @cancel="closeModal"
      destroy-on-close
    >
      <a-form layout="vertical">
        <a-form-item label="Device">
          <a-select
            v-model:value="form.device"
            show-search
            placeholder="Select a device"
            :options="deviceOptions"
            :filter-option="filterDeviceOption"
            :disabled="!!editingConfig"
            style="width: 100%"
          />
        </a-form-item>

        <a-form-item label="Check Interval (minutes)">
          <a-input-number
            v-model:value="form.check_interval_minutes"
            :min="5"
            style="width: 100%"
          />
          <small style="color: #888">Minimum 5 minutes</small>
        </a-form-item>

        <a-form-item>
          <a-space direction="vertical">
            <a-checkbox v-model:checked="form.alert_on_offline">Alert on Offline</a-checkbox>
            <a-checkbox v-model:checked="form.alert_on_idle">Alert on Idle</a-checkbox>
            <a-space>
              <a-switch v-model:checked="form.is_active" size="small" />
              <span>Active</span>
            </a-space>
          </a-space>
        </a-form-item>

        <a-form-item label="Recipients">
          <div class="recipients-list mb-2">
            <!-- Edit mode: recipients saved on server -->
            <template v-if="editingConfig">
              <a-tag
                v-for="r in editingConfig.recipients"
                :key="r.id"
                closable
                style="margin-bottom: 6px"
                @close="handleDeleteRecipient(editingConfig.id, r.id)"
              >
                {{ r.email }}
              </a-tag>
              <span v-if="!editingConfig.recipients || editingConfig.recipients.length === 0" class="text-muted">
                No recipients yet
              </span>
            </template>
            <!-- Add mode: pending emails (submitted after config is created) -->
            <template v-else>
              <a-tag
                v-for="(email, idx) in pendingRecipients"
                :key="idx"
                closable
                style="margin-bottom: 6px"
                @close="pendingRecipients.splice(idx, 1)"
              >
                {{ email }}
              </a-tag>
              <span v-if="pendingRecipients.length === 0" class="text-muted">
                No recipients added yet
              </span>
            </template>
          </div>
          <div class="d-flex" style="gap: 8px">
            <a-input
              v-model:value="newRecipientEmail"
              placeholder="Email address"
              style="flex: 1"
              @keydown.enter="editingConfig ? handleAddRecipient(editingConfig.id) : handleAddPendingRecipient()"
            />
            <a-button
              type="primary"
              :loading="isAddingRecipient"
              @click="editingConfig ? handleAddRecipient(editingConfig.id) : handleAddPendingRecipient()"
            >
              Add
            </a-button>
          </div>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import AlertConfigService from 'src/services/alertConfig';
import { mapActions, mapGetters } from 'vuex';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default {
  name: 'EmailAlerts',
  inject: ['toast'],

  data() {
    return {
      configs: [],
      isFetching: false,
      showModal: false,
      isSubmitting: false,
      editingConfig: null,
      form: {
        device: null,
        alert_on_offline: true,
        alert_on_idle: false,
        check_interval_minutes: 15,
        is_active: true,
      },
      pendingRecipients: [],
      newRecipientEmail: '',
      isAddingRecipient: false,
      columns: [
        { title: 'Device', key: 'device', dataIndex: 'device' },
        { title: 'Alert on Offline', key: 'alert_on_offline' },
        { title: 'Alert on Idle', key: 'alert_on_idle' },
        { title: 'Interval (min)', key: 'check_interval_minutes', dataIndex: 'check_interval_minutes' },
        { title: 'Status', key: 'is_active' },
        { title: 'Recipients', key: 'recipients' },
        { title: 'Actions', key: 'actions' },
      ],
    };
  },

  computed: {
    ...mapGetters(['organization', 'devices']),
    deviceOptions() {
      return (this.devices || []).map((d) => ({
        value: d.Device,
        label: d.display_name || d.Serial_number,
      }));
    },
  },

  created() {
    if (!this.devices || this.devices.length === 0) {
      this.getOrgDevices(this.organization);
    }
    this.fetchConfigs();
  },

  methods: {
    ...mapActions(['getOrgDevices']),
    async fetchConfigs() {
      this.isFetching = true;
      const [error, data] = await AlertConfigService.fetchAlertConfigs(this.organization);
      this.isFetching = false;
      if (error) {
        this.toast.error('Failed to load alert configs');
        return;
      }
      this.configs = data;
    },

    getDeviceLabel(deviceId) {
      const device = (this.devices || []).find((d) => d.Device === deviceId);
      if (!device) return `Device #${deviceId}`;
      return device.display_name || device.Serial_number;
    },

    filterDeviceOption(input, option) {
      return option.label.toLowerCase().includes(input.toLowerCase());
    },

    openAddModal() {
      this.editingConfig = null;
      this.form = {
        device: null,
        alert_on_offline: true,
        alert_on_idle: false,
        check_interval_minutes: 15,
        is_active: true,
      };
      this.pendingRecipients = [];
      this.newRecipientEmail = '';
      this.showModal = true;
    },

    openEditModal(config) {
      this.editingConfig = config;
      this.form = {
        device: config.device,
        alert_on_offline: config.alert_on_offline,
        alert_on_idle: config.alert_on_idle,
        check_interval_minutes: config.check_interval_minutes,
        is_active: config.is_active,
      };
      this.newRecipientEmail = '';
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.editingConfig = null;
      this.pendingRecipients = [];
      this.newRecipientEmail = '';
    },

    async handleSubmit() {
      this.isSubmitting = true;
      if (this.editingConfig) {
        const payload = {
          alert_on_offline: this.form.alert_on_offline,
          alert_on_idle: this.form.alert_on_idle,
          check_interval_minutes: this.form.check_interval_minutes,
          is_active: this.form.is_active,
        };
        const [error, data] = await AlertConfigService.updateAlertConfig(this.editingConfig.id, payload);
        this.isSubmitting = false;
        if (error) return;
        const idx = this.configs.findIndex((c) => c.id === this.editingConfig.id);
        if (idx !== -1) {
          this.configs[idx] = { ...this.configs[idx], ...data };
          this.configs = [...this.configs];
        }
        this.toast.success('Alert config updated');
      } else {
        const [error, data] = await AlertConfigService.createAlertConfig({ ...this.form });
        if (error) {
          this.isSubmitting = false;
          return;
        }
        if (this.pendingRecipients.length > 0) {
          await Promise.all(
            this.pendingRecipients.map((email) => AlertConfigService.addRecipient(data.id, email))
          );
          await this.fetchConfigs();
        } else {
          this.configs = [...this.configs, { ...data, recipients: [] }];
        }
        this.isSubmitting = false;
        this.toast.success('Alert config created');
      }
      this.closeModal();
    },

    async handleDelete(id) {
      const [error] = await AlertConfigService.deleteAlertConfig(id);
      if (error) {
        this.toast.error('Failed to delete alert config');
        return;
      }
      this.configs = this.configs.filter((c) => c.id !== id);
      this.toast.success('Alert config deleted');
    },

    handleAddPendingRecipient() {
      const email = this.newRecipientEmail.trim();
      if (!EMAIL_RE.test(email)) {
        this.toast.error('Please enter a valid email address');
        return;
      }
      if (this.pendingRecipients.includes(email)) {
        this.toast.error('Email already added');
        return;
      }
      this.pendingRecipients.push(email);
      this.newRecipientEmail = '';
    },

    async handleAddRecipient(configId) {
      const email = this.newRecipientEmail.trim();
      if (!EMAIL_RE.test(email)) {
        this.toast.error('Please enter a valid email address');
        return;
      }
      this.isAddingRecipient = true;
      const [error, data] = await AlertConfigService.addRecipient(configId, email);
      this.isAddingRecipient = false;
      if (error) return;
      this.newRecipientEmail = '';
      // editingConfig IS the same object reference as configs[n], so only update once
      const config = this.configs.find((c) => c.id === configId);
      if (config) config.recipients = [...(config.recipients || []), data];
    },

    async handleDeleteRecipient(configId, recipientId) {
      const [error] = await AlertConfigService.deleteRecipient(recipientId);
      if (error) {
        this.toast.error('Failed to remove recipient');
        return;
      }
      // editingConfig IS the same object reference as configs[n], so only update once
      const config = this.configs.find((c) => c.id === configId);
      if (config) config.recipients = config.recipients.filter((r) => r.id !== recipientId);
    },
  },
};
</script>

<style scoped>
.recipients-list {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}
</style>
